// src/components/forms/RadioGroup.tsx
import { FC } from "react";
import { RadioGroup as HeadlessRadioGroup } from "@headlessui/react";
import { Option } from "./types";

interface Props<T = string | number> {
  label: string;
  options: Option<T>[];
  value: T;
  onChange: (val: T) => void;
  
  className?: string;
  /** Number of columns for horizontal layout. Default is 1 (Vertical) */
  cols?: number;
}

export const RadioGroupParametriz = <T extends string | number>({ 
  label, 
  options, 
  value, 
  onChange, 
  className,
  cols = 1 
}: Props<T>) => {
  
  // Dynamic grid configuration based on the 'cols' prop
  const gridStyles = {
    display: 'grid',
    gap: '0.625rem', // 10px
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* Colorful Indigo Label */}
      <span className="text-sm font-bold uppercase text-slate-200/70 dark:text-indigo-200 ml-1">
        {label}
      </span>

      <HeadlessRadioGroup value={value} onChange={onChange}>
        <div style={gridStyles}>
          {options.map((opt) => (
            <HeadlessRadioGroup.Option key={opt.value} value={opt.value} className="h-full">
              {({ checked }) => (
                <div
                  className={`relative cursor-pointer h-full rounded-xl px-4 py-2.5 border-2 transition-all duration-200 transform active:scale-[0.96] flex items-center
                    ${checked 
                      ? "border-transparent bg-white dark:bg-zinc-900 shadow-md shadow-indigo-500/10" 
                      : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-indigo-300 dark:hover:border-indigo-700"
                    }`}
                >
                  {/* The Gradient Border effect when checked */}
                  {checked && (
                    <div className="absolute -inset-[2px] bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl -z-10 animate-in fade-in duration-300"></div>
                  )}

                  <div className="flex items-center gap-2.5">
                    {/* Custom Radio Circle (Slightly smaller for horizontal layout) */}
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors shrink-0
                      ${checked ? "border-indigo-500" : "border-zinc-400 dark:border-zinc-600"}`}>
                      <div className={`w-2 h-2 rounded-full transition-all transform
                        ${checked 
                          ? "bg-gradient-to-tr from-indigo-500 to-pink-500 scale-100" 
                          : "bg-transparent scale-0"
                        }`} 
                      />
                    </div>

                    <span className={`text-xs font-bold transition-colors truncate
                      ${checked ? "text-indigo-700 dark:text-indigo-300" : "text-zinc-600 dark:text-zinc-400"}`}>
                      {opt.label}
                    </span>
                  </div>
                </div>
              )}
            </HeadlessRadioGroup.Option>
          ))}
        </div>
      </HeadlessRadioGroup>
    </div>
  );
};